import { Sparkles } from "lucide-react";
import {
  bundles,
  priceTiers,
  DRINKS_PER_TUBE,
  type Bundle,
  type OrderMode,
} from "@/data/pricing";

interface BundleShopProps {
  mode: OrderMode;
  onSelect: (bundle: Bundle) => void;
}

const BundleShop = ({ mode, onSelect }: BundleShopProps) => {
  const basePricePerTube = priceTiers[0].purchasePrice / priceTiers[0].tubes;

  return (
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">
        Välj ett paket
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {bundles.map((bundle) => {
          const tier = priceTiers.find((t) => t.tubes === bundle.tubes);
          if (!tier) return null;

          const price =
            mode === "purchase" ? tier.purchasePrice : tier.subscriptionPrice;
          const originalPrice = Math.round(basePricePerTube * bundle.tubes);
          const savings = Math.max(0, originalPrice - price);
          const drinks = bundle.tubes * DRINKS_PER_TUBE;

          return (
            <button
              key={bundle.id}
              type="button"
              onClick={() => onSelect(bundle)}
              className={`group relative text-left rounded-2xl overflow-hidden aspect-square p-5 flex flex-col justify-between bg-gradient-to-br ${bundle.gradient} transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                bundle.highlighted ? "ring-2 ring-foreground/40" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-col gap-1.5">
                  {savings > 0 && (
                    <span className="inline-flex w-fit items-center bg-foreground/90 text-background text-[9px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full">
                      Spara {savings} kr
                    </span>
                  )}
                </div>

                {bundle.highlighted && (
                  <span className="inline-flex items-center gap-1 bg-foreground text-background text-[9px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full">
                    <Sparkles className="w-2.5 h-2.5" />
                    Populär
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <h3 className="text-lg md:text-xl font-black uppercase text-foreground tracking-tight leading-tight">
                  {bundle.name}
                </h3>
                <p className="text-[11px] text-foreground/70 tracking-wide leading-snug">
                  {bundle.people}
                </p>
                <div className="flex items-baseline gap-2 flex-wrap mt-1">
                  <span className="text-base font-black text-foreground">
                    {price} kr
                    {mode === "subscription" && (
                      <span className="text-[10px] font-medium">/mån</span>
                    )}
                  </span>
                </div>
                <p className="text-[10px] text-foreground/60 tracking-wide">
                  {bundle.tubes} tuber · {drinks} drycker
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BundleShop;
